import type { ElementType, ReactNode } from "react";
import Hero from "../../components/Hero";
import HeroBanner from "../../components/HeroBanner";
import Image from "../../components/Image";
import VideoPlayer from "../../components/VideoPlayer/VideoPlayer";
import VimeoPlayer from "../../components/VimeoPlayer";
import GridItem from "../../components/general/GridItem";
import Hotspots from "../../components/general/Hotspots";
import type { IconType } from "../../components/general/Icon/Icon";
import { getUrlFromStoryblokLink } from "../../utils/storyblok/urls";
import { isImage, isVideo } from "../../utils/storyblok/utils";
import { mapHotspotsToHotspotsProps } from "../../utils/storyblok/product-variant";

const TextOnContentModule = (module: Queries.TextOnContentModuleFragment) => {
	return <GridItem>{mapModuleToHeroComponent(module)}</GridItem>;
};

const getSizes = (heroBanner: boolean, mobile: boolean): string => {
	if (mobile) return "75vw";
	return heroBanner ? "50vw" : "75vw";
};

export const mapModuleToHeroComponent = (
	{
		_uid,
		background_color,
		background_image,
		desktop_vimeo,
		mobile_image,
		mobile_vimeo,
		show_vimeo_controls,
		label,
		layout: layouts,
		inverted,
		hotspots,
		image_ratio,
	}: Queries.TextOnContentModuleFragment,
	labelHeadingAs?: ElementType,
	isInDoubleTextBlock?: boolean,
) => {
	const fileName = background_image?.filename;
	const desktopVimeoID = desktop_vimeo?.vimeo_oembed?.response?.video_id;
	const mobileVimeoID = mobile_vimeo?.vimeo_oembed?.response?.video_id;
	const layout = layouts?.[0];
	const textLayout = layout?.component;
	const isHeroBanner = textLayout === "text_on_color_block";
	const vimeoControls = show_vimeo_controls
		? {
				controls: true,
				background: false,
			}
		: {};

	const getMedia = (
		asset: {
			filename: string | null | undefined;
			alt: string | null | undefined;
		} | null,
		sizes: string,
		priority = true,
	) => {
		if (!asset?.filename) return null;

		if (isImage(asset.filename)) {
			return (
				<Image
					fill
					src={asset.filename}
					alt={asset.alt ?? undefined}
					priority={priority}
					sizes={sizes}
				/>
			);
		}

		if (isVideo(asset.filename)) {
			return <VideoPlayer videoUrl={asset.filename} />;
		}
		return null;
	};

	const desktopMedia = desktopVimeoID ? (
		<VimeoPlayer
			videoID={desktopVimeoID}
			playerID={`${_uid}_desktop`}
			posterUrl={desktop_vimeo.vimeo_oembed.response.thumbnail_url ?? ""}
			title={desktop_vimeo.vimeo_oembed.response.title ?? "Vimeo Video"}
			settings={vimeoControls}
		/>
	) : (
		getMedia(background_image, getSizes(isHeroBanner, false), !isHeroBanner)
	);

	const mobileMedia = mobileVimeoID ? (
		<VimeoPlayer
			videoID={mobileVimeoID}
			playerID={`${_uid}_mobile`}
			posterUrl={mobile_vimeo.vimeo_oembed.response.thumbnail_url ?? ""}
			title={mobile_vimeo.vimeo_oembed.response.title ?? "Vimeo Video"}
			settings={vimeoControls}
		/>
	) : (
		getMedia(
			mobile_image ?? background_image,
			getSizes(isHeroBanner, true),
			!isHeroBanner,
		)
	);

	const conditionalWrapper = (children: ReactNode) => {
		if (desktopVimeoID || mobileVimeoID) return children;
		return (
			<Hotspots name="hotspots" hotspots={mapHotspotsToHotspotsProps(hotspots)}>
				{children}
			</Hotspots>
		);
	};

	if (layouts?.length === 0 && desktopMedia) {
		return (
			<Hero
				variant="onlyImage"
				isInDoubleTextBlock={isInDoubleTextBlock}
				mobileImageRatio={image_ratio === "5:7" ? "5:7" : "1:1"}
				key={_uid}
				media={conditionalWrapper(desktopMedia)}
				mobileMedia={mobileMedia && conditionalWrapper(mobileMedia)}
			/>
		);
	}

	const title = layout?.title;
	const use_gradient =
		layout?.component === "text_on_color_block"
			? Boolean(layout.use_gradient)
			: false;
	const firstButton = layout?.button?.[0];

	const buttonProps = {
		href: getUrlFromStoryblokLink(firstButton?.link),
		target: firstButton?.link?.target ?? "_self",
		label: firstButton?.label ?? "",
		icon: firstButton?.icon as IconType,
	};

	const heroBannerVariant = use_gradient ? "gradient" : "block";
	const heroVariant =
		fileName || desktopMedia ? "backgroundImage" : "backgroundColor";

	const props = {
		title: title ?? "",
		label: label ?? "",
		media: desktopMedia,
		mobileMedia,
		inverted: inverted ?? undefined,
		backgroundColor: isHeroBanner
			? (layout?.background_color ?? undefined)
			: (background_color ?? undefined),
	};
	return isHeroBanner ? (
		<HeroBanner
			{...props}
			key={_uid}
			mobileImageRatio={image_ratio === "5:7" ? "5:7" : "1:1"}
			buttonProps={buttonProps}
			variant={heroBannerVariant}
			isInDoubleTextBlock={isInDoubleTextBlock}
		/>
	) : (
		<Hero
			{...props}
			key={_uid}
			mobileImageRatio={image_ratio === "5:7" ? "5:7" : "1:1"}
			buttonProps={firstButton ? buttonProps : undefined}
			variant={heroVariant}
			labelHeadingAs={labelHeadingAs}
			isInDoubleTextBlock={isInDoubleTextBlock}
		/>
	);
};

export default TextOnContentModule;
